import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';
import {
  SettingsIcon,
  ProductsIcon,
  OrdersIcon,
  UsersStatIcon,
  FinanceIcon,
  InvoicesIcon,
  StoreCreditsIcon,
  CategoriesIcon,
  CouponsIcon,
  PromotionsIcon,
  LoyaltyIcon,
  GiftCardsIcon,
  ShippingIcon,
  PaymentIcon,
  EmailIcon,
  PagesIcon,
  MarketplaceIcon,
  BackupIcon,
  ErrorIcon,
  ReportsIcon,
  ViewIcon,
} from '../components/Icons';
import './AdminDashboard.css';

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  
  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/stats');
      return res.data;
    },
  });

  const { data: recentOrders } = useQuery({
    queryKey: ['admin-recent-orders'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/orders?limit=5&page=1');
      return res.data;
    },
  });

  if (isLoading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Kraunama...</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' €';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('lt-LT', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Laukiama', class: 'status-pending' },
      processing: { label: 'Ruošiamas', class: 'status-processing' },
      shipped: { label: 'Išsiųstas', class: 'status-shipped' },
      delivered: { label: 'Įvykdytas', class: 'status-delivered' },
      cancelled: { label: 'Atšauktas', class: 'status-cancelled' },
    };
    const statusInfo = statusMap[status] || { label: status, class: 'status-pending' };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <h1>
              <SettingsIcon />
              Administravimo panelė
            </h1>
            <p className="dashboard-subtitle">Valdykite parduotuvę vienoje vietoje</p>
          </div>
          <div className="header-actions">
            <Link to="/admin/orders" className="btn btn-primary btn-sm">
              <OrdersIcon />
              Visi užsakymai
            </Link>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Apžvalga
        </button>
        <button
          className={activeTab === 'orders' ? 'active' : ''}
          onClick={() => setActiveTab('orders')}
        >
          Užsakymai
        </button>
        <button
          className={activeTab === 'quick-actions' ? 'active' : ''}
          onClick={() => setActiveTab('quick-actions')}
        >
          Greitieji veiksmai
        </button>
        <button
          className={activeTab === 'functions' ? 'active' : ''}
          onClick={() => setActiveTab('functions')}
        >
          Visos funkcijos
        </button>
      </div>

      {/* Tab Content */}
      <div className="dashboard-content">
        {activeTab === 'overview' && (
          <>
            {/* Statistika */}
            <div className="stats-grid">
              <div className="stat-card stat-card-primary">
                <div className="stat-card-header">
                  <div className="stat-icon">
                    <ProductsIcon />
                  </div>
                  <h3>Produktai</h3>
                </div>
                <p className="stat-value">{stats?.total_products || 0}</p>
                <p className="stat-label">Iš viso produktų</p>
              </div>

              <div className="stat-card stat-card-success">
                <div className="stat-card-header">
                  <div className="stat-icon">
                    <OrdersIcon />
                  </div>
                  <h3>Užsakymai</h3>
                </div>
                <p className="stat-value">{stats?.total_orders || 0}</p>
                <p className="stat-label">Iš viso užsakymų</p>
              </div>

              <div className="stat-card stat-card-info">
                <div className="stat-card-header">
                  <div className="stat-icon">
                    <UsersStatIcon />
                  </div>
                  <h3>Vartotojai</h3>
                </div>
                <p className="stat-value">{stats?.total_users || 0}</p>
                <p className="stat-label">Registruotų vartotojų</p>
              </div>

              <div className="stat-card stat-card-warning">
                <div className="stat-card-header">
                  <div className="stat-icon">
                    <FinanceIcon />
                  </div>
                  <h3>Pajamos</h3>
                </div>
                <p className="stat-value">{formatCurrency(stats?.total_revenue)}</p>
                <p className="stat-label">Bendros pajamos</p>
              </div>
            </div>
          </>
        )}

        {activeTab === 'orders' && (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>
                <OrdersIcon />
                Naujausi užsakymai
              </h2>
              <Link to="/admin/orders" className="section-link">
                Visi užsakymai <ViewIcon />
              </Link>
            </div>
            <div className="recent-orders">
              {recentOrders?.orders && recentOrders.orders.length > 0 ? (
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>Užsakymo Nr.</th>
                      <th>Klientas</th>
                      <th>Data</th>
                      <th>Suma</th>
                      <th>Statusas</th>
                      <th>Veiksmai</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.orders.map((order) => (
                      <tr key={order.id}>
                        <td>
                          <Link to={`/admin/orders/${order.id}`} className="order-link">
                            {order.order_number || `#${order.id}`}
                          </Link>
                        </td>
                        <td>{order.customer_name || order.customer_email || 'Svečias'}</td>
                        <td>{formatDate(order.created_at)}</td>
                        <td className="amount">{formatCurrency(order.total_amount)}</td>
                        <td>{getStatusBadge(order.status)}</td>
                        <td>
                          <Link
                            to={`/admin/orders/${order.id}`}
                            className="btn-icon btn-icon-secondary"
                            title="Peržiūrėti"
                          >
                            <ViewIcon />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state">
                  <OrdersIcon />
                  <p>Nėra užsakymų</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'quick-actions' && (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>
                <SettingsIcon />
                Greitieji veiksmai
              </h2>
            </div>
            <div className="quick-actions-grid">
              <Link to="/admin/products/new" className="quick-action-card">
                <ProductsIcon />
                <span>Naujas produktas</span>
              </Link>
              <Link to="/admin/orders" className="quick-action-card">
                <OrdersIcon />
                <span>Užsakymai</span>
              </Link>
              <Link to="/admin/invoices" className="quick-action-card">
                <InvoicesIcon />
                <span>Sąskaitos</span>
              </Link>
              <Link to="/admin/store-credits" className="quick-action-card">
                <StoreCreditsIcon />
                <span>Kreditai</span>
              </Link>
              <Link to="/admin/categories" className="quick-action-card">
                <CategoriesIcon />
                <span>Kategorijos</span>
              </Link>
              <Link to="/admin/coupons" className="quick-action-card">
                <CouponsIcon />
                <span>Kuponai</span>
              </Link>
            </div>
          </div>
        )}

        {activeTab === 'functions' && (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>
                <SettingsIcon />
                Visos funkcijos
              </h2>
            </div>
          <div className="admin-menu">
            <Link to="/admin/orders" className="admin-menu-card">
              <div className="menu-icon">
                <OrdersIcon />
              </div>
              <h3>Užsakymų valdymas</h3>
              <p>Peržiūrėti ir valdyti užsakymus</p>
            </Link>
            <Link to="/admin/products" className="admin-menu-card">
              <div className="menu-icon">
                <ProductsIcon />
              </div>
              <h3>Produktų valdymas</h3>
              <p>Pridėti, redaguoti ir valdyti produktus</p>
            </Link>
            <Link to="/admin/invoices" className="admin-menu-card">
              <div className="menu-icon">
                <InvoicesIcon />
              </div>
              <h3>Sąskaitos faktūros</h3>
              <p>Valdyti sąskaitas faktūras</p>
            </Link>
            <Link to="/admin/store-credits" className="admin-menu-card">
              <div className="menu-icon">
                <StoreCreditsIcon />
              </div>
              <h3>Store Credits</h3>
              <p>Valdyti parduotuvės kreditus</p>
            </Link>
            <Link to="/admin/orders/reports" className="admin-menu-card">
              <div className="menu-icon">
                <ReportsIcon />
              </div>
              <h3>Ataskaitos</h3>
              <p>Užsakymų statistika ir ataskaitos</p>
            </Link>
            <Link to="/admin/finance-reports" className="admin-menu-card">
              <div className="menu-icon">
                <FinanceIcon />
              </div>
              <h3>Finansinės ataskaitos</h3>
              <p>Pardavimai, marža, nuolaidos, grąžinimai</p>
            </Link>
            <Link to="/admin/store-settings" className="admin-menu-card">
              <div className="menu-icon">
                <SettingsIcon />
              </div>
              <h3>Parduotuvės duomenys</h3>
              <p>Nustatyti parduotuvės duomenis dokumentams</p>
            </Link>
            <Link to="/admin/payment-methods" className="admin-menu-card">
              <div className="menu-icon">
                <PaymentIcon />
              </div>
              <h3>Mokėjimo priemonės</h3>
              <p>Konfigūruoti mokėjimo priemones</p>
            </Link>
            <Link to="/admin/shipping-methods" className="admin-menu-card">
              <div className="menu-icon">
                <ShippingIcon />
              </div>
              <h3>Pristatymo metodai</h3>
              <p>Valdyti pristatymo būdus ir taisykles</p>
            </Link>
            <Link to="/admin/courier-integrations" className="admin-menu-card">
              <div className="menu-icon">
                <ShippingIcon />
              </div>
              <h3>Kurjerių integracijos</h3>
              <p>Konfigūruoti kurjerių API</p>
            </Link>
            <Link to="/admin/categories" className="admin-menu-card">
              <div className="menu-icon">
                <CategoriesIcon />
              </div>
              <h3>Prekių kategorijos</h3>
              <p>Valdyti prekių kategorijas</p>
            </Link>
            <Link to="/admin/coupons" className="admin-menu-card">
              <div className="menu-icon">
                <CouponsIcon />
              </div>
              <h3>Kuponai</h3>
              <p>Valdyti nuolaidų kuponus</p>
            </Link>
            <Link to="/admin/promotions" className="admin-menu-card">
              <div className="menu-icon">
                <PromotionsIcon />
              </div>
              <h3>Akcijų taisyklės</h3>
              <p>Valdyti akcijų taisykles</p>
            </Link>
            <Link to="/admin/loyalty" className="admin-menu-card">
              <div className="menu-icon">
                <LoyaltyIcon />
              </div>
              <h3>Lojalumo taškai</h3>
              <p>Valdyti lojalumo taškų sistemą</p>
            </Link>
            <Link to="/admin/gift-cards" className="admin-menu-card">
              <div className="menu-icon">
                <GiftCardsIcon />
              </div>
              <h3>Dovanų kortelės</h3>
              <p>Valdyti dovanų korteles</p>
            </Link>
            <Link to="/admin/email-automation" className="admin-menu-card">
              <div className="menu-icon">
                <EmailIcon />
              </div>
              <h3>El. laiškų automatizacija</h3>
              <p>Valdyti email šablonus ir kampanijas</p>
            </Link>
            <Link to="/admin/pages" className="admin-menu-card">
              <div className="menu-icon">
                <PagesIcon />
              </div>
              <h3>Puslapių valdymas</h3>
              <p>Valdyti puslapius (Apie, Pristatymas, Grąžinimas, DUK)</p>
            </Link>
            <Link to="/admin/marketplace-integrations" className="admin-menu-card">
              <div className="menu-icon">
                <MarketplaceIcon />
              </div>
              <h3>Marketplace'ų integracijos</h3>
              <p>Konfigūruoti Amazon, eBay, Etsy integracijas</p>
            </Link>
            <Link to="/admin/backups" className="admin-menu-card">
              <div className="menu-icon">
                <BackupIcon />
              </div>
              <h3>Atsarginės kopijos</h3>
              <p>Kurti ir valdyti atsargines kopijas</p>
            </Link>
            <Link to="/admin/error-logs" className="admin-menu-card">
              <div className="menu-icon">
                <ErrorIcon />
              </div>
              <h3>Klaidos žurnalai</h3>
              <p>Peržiūrėti sistemos klaidas ir monitoringą</p>
            </Link>
            <Link to="/admin/smtp-settings" className="admin-menu-card">
              <div className="menu-icon">
                <EmailIcon />
              </div>
              <h3>SMTP Nustatymai</h3>
              <p>Konfigūruoti el. pašto siuntimo nustatymus</p>
            </Link>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
